import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorText: string;
}

function describeError(error: unknown): string {
  if (error && typeof error === "object") {
    const details = error as { name?: unknown; message?: unknown; stack?: unknown };
    const name = typeof details.name === "string" && details.name ? details.name : "Error";
    let message = typeof details.message === "string" ? details.message : "";
    if (!message) {
      try {
        message = JSON.stringify(error, null, 2) || String(error);
      } catch {
        message = String(error);
      }
    }
    const stack = typeof details.stack === "string" ? details.stack : "";
    return `${name}: ${message}${stack ? `\n\nStack:\n${stack}` : ""}`;
  }
  return `Non-Error value: ${String(error)}`;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorText: "" };
  }

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, errorText: describeError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Home, Twice hit a rendering error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main
          role="alert"
          className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground sm:p-10"
        >
          <section className="w-full max-w-2xl rounded-xl border border-border bg-card p-6 shadow-lg sm:p-8">
            <AlertTriangle
              size={36}
              aria-hidden="true"
              className="mb-4 text-destructive"
            />
            <h1 className="mb-3 text-2xl font-semibold">Home, Twice hit a problem.</h1>
            <p className="mb-5 leading-relaxed text-muted-foreground">
              Please copy the error below and share it with the site owner.
            </p>
            <pre className="mb-6 max-h-[45vh] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-background p-4 text-sm text-foreground">
              {this.state.errorText}
            </pre>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2",
              )}
            >
              <RotateCcw size={16} aria-hidden="true" />
              Reload page
            </button>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
